import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Award, Users, BookOpen, Trophy, ArrowRight, Calendar, MapPin, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isAchievementModalOpen, setIsAchievementModalOpen] = useState(false);

  const [educationLevels, setEducationLevels] = useState([]);
  const [coreValues, setCoreValues] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [heroSlides, setHeroSlides] = useState([
    {
      image: 'https://images.unsplash.com/photo-1603958956194-cf9718dba4b1',
      title: 'Geleceğe Güvenle Yürüyün',
      subtitle: 'Modern eğitim anlayışı ile başarıya giden yolda yanınızdayız',
    },
    {
      image: 'https://images.unsplash.com/photo-1730106443463-0fb1512c5e60',
      title: 'Kaliteli Eğitim, Parlak Gelecek',
      subtitle: 'Akademik mükemmellik ve kişisel gelişim bir arada',
    },
  ]);

  useEffect(() => {
    fetchNews();
    fetchEvents();
    fetchAchievements();
    fetchEducationLevels();
    fetchCoreValues();
    fetchFaqs();
    fetchHeroImages();
  }, []);

  const fetchHeroImages = async () => {
    try {
      const response = await fetch('http://localhost/api/settings.php?key=homepage_hero_images', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      if (data.setting_value) {
        try {
          const parsed = typeof data.setting_value === 'string' 
            ? JSON.parse(data.setting_value) 
            : data.setting_value;
          if (Array.isArray(parsed) && parsed.length > 0) {
            setHeroSlides(parsed);
          }
        } catch (e) {
          console.error('Error parsing hero images:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
    }
  };

  const fetchEducationLevels = async () => {
    try {
      const response = await fetch('http://localhost/api/education.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching education levels:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      // Sadece aktif olanları al - tüm olası formatları kontrol et
      const activeLevels = data.filter(item => {
        const isActive = item.is_active;
        // Aktif olmayanları filtrele
        if (isActive === false || isActive === 0 || isActive === '0' || isActive === null || isActive === undefined || isActive === '') {
          return false;
        }
        // Aktif olanları al
        return isActive === true || isActive === 1 || isActive === '1';
      });
      setEducationLevels(activeLevels);
    } catch (error) {
      console.error('Error fetching education levels:', error);
    }
  };

  const fetchCoreValues = async () => {
    try {
      const response = await fetch('http://localhost/api/core-values.php?active=1', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching core values:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      // Sadece aktif olanları al - tüm olası formatları kontrol et
      const activeCoreValues = data.filter(item => {
        const isActive = item.is_active;
        // Aktif olmayanları filtrele
        if (isActive === false || isActive === 0 || isActive === '0' || isActive === null || isActive === undefined || isActive === '') {
          return false;
        }
        // Aktif olanları al
        return isActive === true || isActive === 1 || isActive === '1';
      });
      setCoreValues(activeCoreValues);
    } catch (error) {
      console.error('Error fetching core values:', error);
    }
  };

  const fetchFaqs = async () => {
    try {
      const response = await fetch('http://localhost/api/faqs.php?active=1', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching FAQs:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      
      // Sadece aktif olanları al - kesinlikle sadece is_active = 1 veya '1' olanları
      const activeFaqs = data.filter(item => {
        const isActive = item.is_active;
        // Sadece kesinlikle 1 veya '1' olanları kabul et, başka hiçbir şeyi kabul etme
        return isActive === 1 || isActive === '1';
      });
      
      // Duplicate'leri filtrele (aynı soruya sahip olanları bir kez göster)
      const uniqueFaqs = [];
      const seenQuestions = new Set();
      
      for (const faq of activeFaqs) {
        const questionKey = faq.question?.toLowerCase().trim();
        if (questionKey && !seenQuestions.has(questionKey)) {
          seenQuestions.add(questionKey);
          uniqueFaqs.push(faq);
        }
      }
      
      setFaqs(uniqueFaqs);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      Award: <Award className="w-8 h-8" />,
      Users: <Users className="w-8 h-8" />,
      BookOpen: <BookOpen className="w-8 h-8" />,
      Trophy: <Trophy className="w-8 h-8" />,
    };
    return icons[iconName] || <Award className="w-8 h-8" />;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (heroSlides.length || 1));
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const fetchNews = async () => {
    try {
      const response = await fetch('http://localhost/api/news.php?published=1', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching news:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      // Sadece yayında olanları al - tüm olası formatları kontrol et
      const publishedNews = data.filter(item => {
        const isPublished = item.is_published;
        // Yayında olmayanları filtrele
        if (isPublished === false || isPublished === 0 || isPublished === '0' || isPublished === null || isPublished === undefined || isPublished === '') {
          return false;
        }
        // Yayında olanları al
        return isPublished === true || isPublished === 1 || isPublished === '1';
      });
      setNews(publishedNews);
    } catch (error) {
      console.error('Error fetching news:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await fetch('http://localhost/api/events.php?published=1', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching events:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      // Sadece yayında olanları al - tüm olası formatları kontrol et
      const publishedEvents = data.filter(item => {
        const isPublished = item.is_published;
        // Yayında olmayanları filtrele
        if (isPublished === false || isPublished === 0 || isPublished === '0' || isPublished === null || isPublished === undefined || isPublished === '') {
          return false;
        }
        // Yayında olanları al
        return isPublished === true || isPublished === 1 || isPublished === '1';
      });
      setEvents(publishedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchAchievements = async () => {
    try {
      const response = await fetch('http://localhost/api/achievements.php?published=1', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching achievements:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      // Sadece yayında olanları al - tüm olası formatları kontrol et
      const publishedAchievements = data.filter(item => {
        const isPublished = item.is_published;
        // Yayında olmayanları filtrele
        if (isPublished === false || isPublished === 0 || isPublished === '0' || isPublished === null || isPublished === undefined || isPublished === '') {
          return false;
        }
        // Yayında olanları al
        return isPublished === true || isPublished === 1 || isPublished === '1';
      });
      setAchievements(publishedAchievements);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    }
  };

  const handleNewsClick = (newsItem) => {
    setSelectedNews(newsItem);
    setIsNewsModalOpen(true);
  };

  const handleEventClick = (eventItem) => {
    setSelectedEvent(eventItem);
    setIsEventModalOpen(true);
  };

  const handleAchievementClick = (achievementItem) => {
    setSelectedAchievement(achievementItem);
    setIsAchievementModalOpen(true);
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative h-screen mt-20" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-all duration-1000 ${
                index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-red-900/90 via-red-800/80 to-[#C0A062]/60" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(192,160,98,0.3),transparent_50%)]" />
            </div>
          ))}
        </div>

        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-3xl backdrop-blur-sm bg-white/5 p-8 rounded-3xl border border-white/10 shadow-2xl">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight fade-in-up">
                {heroSlides[currentSlide].title}
              </h1>
              <p className="text-xl sm:text-2xl text-gray-100 mb-8 fade-in-up" style={{ animationDelay: '0.2s' }}>
                {heroSlides[currentSlide].subtitle}
              </p>
              <Link
                to="/iletisim"
                data-testid="apply-now-btn"
                className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-[#C0A062] to-[#9a7d4a] text-white text-lg font-bold rounded-full shadow-2xl hover:shadow-[#C0A062]/60 hover:scale-105 transition-all duration-300 fade-in-up group"
                style={{ animationDelay: '0.4s' }}
              >
                Hemen Başvurun
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={24} />
              </Link>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3 bg-black/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/10">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-[#C0A062] w-12' : 'bg-white/50 w-2 hover:w-8 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-24 bg-gradient-to-b from-white via-gray-50 to-white" data-testid="core-values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
              Neden <span className="gold-gradient-text">Serpil Şahin Koleji?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Eğitimde mükemmelliği hedefleyen, değer odaklı yaklaşımımızla geleceğin liderlerini yetiştiriyoruz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.length > 0 ? (
              coreValues.map((value, index) => (
                <div
                  key={value.id || index}
                  data-testid={`core-value-${index}`}
                  className="group relative p-8 rounded-3xl bg-white border-2 border-gray-100 hover:border-[#C0A062] shadow-lg hover:shadow-2xl hover:shadow-[#C0A062]/10 transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#C0A062] to-[#9a7d4a] rounded-t-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#C0A062] to-[#9a7d4a] flex items-center justify-center text-white mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg shadow-[#C0A062]/30">
                    {getIcon(value.icon_name)}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              ))
            ) : (
              <div className="col-span-4 text-center py-12">
                <p className="text-gray-500">Değerler yükleniyor...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Education Levels Section */}
      <section className="py-20 bg-gray-50" data-testid="education-levels-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Eğitim <span className="gold-gradient-text">Kademelerimiz</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              İlkokul'dan Lise'ye kadar her kademede kaliteli eğitim
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {educationLevels.length > 0 ? (
              educationLevels.map((level, index) => {
                const getIcon = (key) => {
                  const icons = {
                    ilkokul: '📚',
                    ortaokul: '🔬',
                    lise: '🎓',
                  };
                  return icons[key] || '📚';
                };
                
                return (
                  <div
                    key={level.id || index}
                    data-testid={`education-level-${index}`}
                    className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300"
                  >
                    <div className="aspect-[3/4] relative">
                      <img
                        src={level.image_url || 'https://images.unsplash.com/photo-1588072432836-e10032774350'}
                        alt={level.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 via-red-900/50 to-transparent" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="text-4xl mb-3">{getIcon(level.level_key)}</div>
                      <h3 className="text-2xl font-bold text-white mb-2">{level.title}</h3>
                      <p className="text-gray-200 text-sm mb-4">{level.description}</p>
                      <Link
                        to="/egitim-kademeleri"
                        className="inline-flex items-center text-[#C0A062] font-semibold text-sm group-hover:text-[#9a7d4a]"
                      >
                        Detaylı Bilgi
                        <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-500">Eğitim kademeleri yükleniyor...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* News & Events Section */}
      <section className="py-20 bg-white" data-testid="news-events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Haberler ve <span className="gold-gradient-text">Etkinlikler</span>
            </h2>
            <p className="text-lg text-gray-600">Kampüsümüzden en son gelişmeler</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* News */}
            <div data-testid="news-section">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-1 h-8 bg-[#C0A062] mr-3"></span>
                Haberler
              </h3>
              <div className="space-y-4">
                {news.length > 0 ? (
                  news.map((item) => (
                    <div
                      key={item.id}
                      data-testid={`news-item-${item.id}`}
                      onClick={() => handleNewsClick(item)}
                      className="flex gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all cursor-pointer hover:shadow-md"
                    >
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1 hover:text-[#C0A062] transition-colors">{item.title}</h4>
                        <p className="text-sm text-gray-600 line-clamp-2">{item.content}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString('tr-TR') : ''}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-8">Henüz haber bulunmamaktadır.</p>
                )}
              </div>
            </div>

            {/* Events */}
            <div data-testid="events-section">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-1 h-8 bg-red-900 mr-3"></span>
                Etkinlikler
              </h3>
              <div className="space-y-4">
                {events.length > 0 ? (
                  events.map((item) => (
                    <div
                      key={item.id}
                      data-testid={`event-item-${item.id}`}
                      onClick={() => handleEventClick(item)}
                      className="flex gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-all cursor-pointer hover:shadow-md"
                    >
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1 hover:text-red-900 transition-colors">{item.title}</h4>
                        <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                        <p className="text-xs text-gray-500 mt-2">{item.event_date}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-8">Henüz etkinlik bulunmamaktadır.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      {achievements.length > 0 && (
        <section className="py-20 bg-gray-50" data-testid="achievements-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Başarılarımız ve <span className="gold-gradient-text">Gurur Tablomuz</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  data-testid={`achievement-item-${achievement.id}`}
                  onClick={() => handleAchievementClick(achievement)}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all cursor-pointer hover:scale-105"
                >
                  {achievement.image_url && (
                    <img
                      src={achievement.image_url}
                      alt={achievement.title}
                      className="w-full h-40 object-cover rounded-lg mb-4"
                    />
                  )}
                  <h4 className="font-bold text-gray-900 mb-2">{achievement.title}</h4>
                  <p className="text-sm text-gray-600 line-clamp-3">{achievement.description}</p>
                  <p className="text-xs text-[#C0A062] mt-3 font-medium">{achievement.achievement_date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-20 bg-gray-50" data-testid="faq-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Sıkça Sorulan <span className="gold-gradient-text">Sorular</span>
              </h2>
              <p className="text-lg text-gray-600">Merak ettiğiniz soruların cevapları</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={faq.id || index}
                  data-testid={`faq-${index}`}
                  className="bg-white rounded-xl shadow-md overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    {expandedFaq === index ? (
                      <ChevronUp className="text-[#C0A062] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="text-[#C0A062] flex-shrink-0" />
                    )}
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-red-800 to-red-900"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(192,160,98,0.2),transparent_60%)]"></div>
        <div className="absolute inset-0 backdrop-blur-3xl bg-black/10"></div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white p-12 rounded-3xl border border-gray-200 shadow-2xl">
            <h2 className="text-5xl sm:text-6xl font-bold mb-8 leading-tight text-gray-900">
              Geleceğin Yolculuğu <span className="text-[#C0A062]">Burada Başlıyor</span>
          </h2>
            <p className="text-2xl text-gray-800 mb-12 max-w-2xl mx-auto">
            Çocuğunuzun parlak geleceği için hemen başvurun ve ailemize katılın
          </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/iletisim"
              data-testid="cta-apply-btn"
                className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-gradient-to-r from-[#C0A062] to-[#9a7d4a] text-white text-lg font-bold rounded-full shadow-2xl hover:shadow-[#C0A062]/50 hover:scale-105 transition-all duration-300"
            >
              Başvuru Yap
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={24} />
            </Link>
            <Link
              to="/iletisim"
              data-testid="cta-contact-btn"
                className="inline-flex items-center justify-center gap-3 px-10 py-5 bg-gray-900 hover:bg-gray-800 text-white text-lg font-bold rounded-full shadow-2xl border-2 border-gray-900 hover:border-gray-800 hover:scale-105 transition-all duration-300"
            >
              Bize Ulaşın
            </Link>
            </div>
          </div>
        </div>
      </section>

      {/* News Detail Modal */}
      <Dialog open={isNewsModalOpen} onOpenChange={setIsNewsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {selectedNews?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedNews && (
            <div className="space-y-6">
              {selectedNews.image_url && (
                <img
                  src={selectedNews.image_url}
                  alt={selectedNews.title}
                  className="w-full h-64 object-cover rounded-lg"
                />
              )}
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {selectedNews.content}
                </p>
              </div>
              {selectedNews.created_at && (
                <div className="flex items-center gap-2 text-sm text-gray-500 pt-4 border-t">
                  <Calendar size={16} />
                  <span>
                    {new Date(selectedNews.created_at).toLocaleDateString('tr-TR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Event Detail Modal */}
      <Dialog open={isEventModalOpen} onOpenChange={setIsEventModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {selectedEvent?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-6">
              {selectedEvent.image_url && (
                <img
                  src={selectedEvent.image_url}
                  alt={selectedEvent.title}
                  className="w-full h-64 object-cover rounded-lg"
                />
              )}
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {selectedEvent.description}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                {selectedEvent.event_date && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-900/10 flex items-center justify-center">
                      <Calendar className="text-red-900" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Tarih</p>
                      <p className="font-semibold text-gray-900">{selectedEvent.event_date}</p>
                    </div>
                  </div>
                )}
                {selectedEvent.event_time && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-900/10 flex items-center justify-center">
                      <Clock className="text-red-900" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Saat</p>
                      <p className="font-semibold text-gray-900">{selectedEvent.event_time}</p>
                    </div>
                  </div>
                )}
                {selectedEvent.location && (
                  <div className="flex items-center gap-3 md:col-span-2">
                    <div className="w-10 h-10 rounded-full bg-red-900/10 flex items-center justify-center">
                      <MapPin className="text-red-900" size={20} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Konum</p>
                      <p className="font-semibold text-gray-900">{selectedEvent.location}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Achievement Detail Modal */}
      <Dialog open={isAchievementModalOpen} onOpenChange={setIsAchievementModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              {selectedAchievement?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedAchievement && (
            <div className="space-y-6">
              {selectedAchievement.image_url && (
                <img
                  src={selectedAchievement.image_url}
                  alt={selectedAchievement.title}
                  className="w-full h-64 object-cover rounded-lg"
                />
              )}
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {selectedAchievement.description}
                </p>
              </div>
              {selectedAchievement.achievement_date && (
                <div className="flex items-center gap-3 pt-4 border-t">
                  <div className="w-10 h-10 rounded-full bg-[#C0A062]/10 flex items-center justify-center">
                    <Trophy className="text-[#C0A062]" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Başarı Tarihi</p>
                    <p className="font-semibold text-gray-900">{selectedAchievement.achievement_date}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default HomePage;